import React, { useState, useEffect, useCallback, useRef } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  Label,
  Input,
  InputGroup,
  InputGroupText,
  Table,
  Card,
  CardHeader,
  CardBody,
  Button,
} from "reactstrap";
import { CSVLink } from "react-csv";
import { ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import PaginatedItems from "../../components/PaginatedItems";
import { fetchAllGroup, forStatus, deleteGroup } from "../../http/http-calls";
import moment from "moment";
import {
  copyToClipBoard,
  formatCurrencyValue,
  showToast,
} from "../../helper-methods";
import SubscriptionPlanModal from "../../components/modals/subscriptionPlanModal";
import TotalRevenueModal from "../../components/modals/TotalRevenueModal";
import CommissionPercentageModal from "../../components/modals/commissionPercentageModal";
import DeleteAlertModal from "../../components/modals/DeleteAlertModal";
import CustomDateRangePicker from "../../components/CustomDateRangePicker";
import { Oval } from "react-loader-spinner";

const GroupsPage = () => {
  // Navigation hook
  const navigate = useNavigate();

  // Refs
  const csvLinkRef = useRef();
  const debounceRef = useRef();

  // States
  const [allGroups, setAllGroups] = useState([]);
  const [groups, setGroups] = useState([]);
  const [count, setCount] = useState("");
  const [itemPerPage, setItemPerPage] = useState("");
  const [payload, setPayload] = useState({
    pageNumber: 1,
    pageSize: 10,
    sortBy: "Created At",
    sortOrder: "high-to-low",
  });

  const [totalGroup, setTotalGroup] = useState(0);
  const [grouprdRevenue, setGroupedRevenue] = useState(0);
  const [subscriptionRevenue, setSubscriptionRevenue] = useState(0);
  const [mediaRevenue, setMediaRevenue] = useState(0);
  const [totalGroupSubCom, setTotalGroupSubCom] = useState(0);
  const [totalgroupChareged, setTotalgroupChareged] = useState(0);
  const [group, setGroup] = useState(undefined);
  const [subscription, setSubscription] = useState([]);
  const [totalRevenue, setTotalRevenue] = useState({});
  const [flag, setFlag] = useState(false); // for button disabled after one click

  const [groupCommision, setGroupCommision] = useState("");
  const [isDeleteGroupOpen, setIsDeleteGroupOpen] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const [isOpen1, setIsOpen1] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [csvLoading, setCsvLoading] = useState(false);
  const [filters, setFilters] = useState({
    dateRange: {
      startDate: null,
      endDate: null,
    },
  });
  const [isEmbeddedLoading, setIsEmbeddedLoading] = useState(false);
  const [isInitialLoad, setIsInitialLoad] = useState(true);

  // Handler for date range changes
  const onDatesChange = (startDate = null, endDate = null) => {
    const newFilters = { ...filters };

    newFilters["dateRange"] = {
      startDate,
      endDate,
    };

    // Handle both dates together to avoid race condition
    const newSearchData = { ...payload };

    if (startDate !== null && startDate !== undefined) {
      newSearchData.startDate = startDate;
    } else {
      delete newSearchData.startDate;
    }

    if (endDate !== null && endDate !== undefined) {
      newSearchData.endDate = endDate;
    } else {
      delete newSearchData.endDate;
    }

    newSearchData.pageNumber = 1;
    setPayload(newSearchData);
    setGroupsData(newSearchData);
    setFilters(newFilters);
  };

  // Toggle for subscription modal
  const toggle = (isOpen = false, item) => {
    setIsOpen(isOpen);
    setSubscription(item?.subscriptionPlan);
  };

  // Toggle for total revenue modal
  const toggle1 = (isModalOpen = false, data) => {
    setIsModalOpen(isModalOpen);
    setTotalRevenue(data);
  };

  // Toggle for commission percentage modal
  const toggle2 = (isOpen1 = false, commision = "") => {
    setIsOpen1(isOpen1);
    setGroupCommision(commision);
  };

  // Toggle for delete group modal
  const toggle3 = (group) => {
    setIsDeleteGroupOpen(!isDeleteGroupOpen);
    setGroup(group);
  };

  // Fetch paginated groups
  const fetchGroups = useCallback(
    async (pageConfig = payload) => {
      try {
        // Only show embedded loader for filtering/searching, not initial load or CSV
        if (
          !pageConfig.isFetchAll &&
          !pageConfig.isInitialLoad &&
          !isInitialLoad
        ) {
          setIsEmbeddedLoading(true);
        }
        const res = await fetchAllGroup(pageConfig);
        setGroups(res.data);
        setCount(res.groupCount);
        setItemPerPage(res.data.length);
        setTotalGroup(res.groupCount);
        setGroupedRevenue(res.totalRevenue);
        setSubscriptionRevenue(res?.totalRevenue - res?.mediaRevenue);
        setMediaRevenue(res?.mediaRevenue);
        setTotalGroupSubCom(res?.totalGroupSubCom);
        setTotalgroupChareged(res?.totalgroupChareged);
        if (
          !pageConfig.isFetchAll &&
          !pageConfig.isInitialLoad &&
          !isInitialLoad
        ) {
          setIsEmbeddedLoading(false);
        }
        if (isInitialLoad || pageConfig.isInitialLoad) setIsInitialLoad(false);
      } catch (error) {
        if (
          !pageConfig.isFetchAll &&
          !pageConfig.isInitialLoad &&
          !isInitialLoad
        ) {
          setIsEmbeddedLoading(false);
        }
        if (isInitialLoad || pageConfig.isInitialLoad) setIsInitialLoad(false);
        showToast(error?.reason, "error");
      }
    },
    [payload, isInitialLoad],
  );

  // Fetch all groups for CSV export
  const fetchAllGroupsForCSV = useCallback(
    async (filterConfig = payload) => {
      try {
        const fullRes = await fetchAllGroup({
          ...filterConfig,
          isFetchAll: true,
        });
        const csvData = fullRes.data?.map((dataPoint) => {
          const fullName = [
            dataPoint.leader?.firstname,
            dataPoint.leader?.lastname,
          ]
            .filter(Boolean)
            .join(" ");
          return {
            name: dataPoint.name,
            isPaid: dataPoint.isPaid,
            isActive: dataPoint.isActive,
            memberCount: dataPoint.memberCount,
            memberCommision: dataPoint.memberCommision,
            leaderName: fullName,
            leaderEmail: dataPoint.leader?.email,
            createdAt: dataPoint.createdAt,
          };
        });
        setAllGroups(csvData);
        return csvData;
      } catch (error) {
        showToast(error?.reason, "error");
      }
    },
    [payload],
  );

  // CSV export handler
  const handleCSVExport = async (e) => {
    e.preventDefault();

    // Format dates properly for API
    const formattedPayload = { ...payload };
    if (formattedPayload.startDate && formattedPayload.startDate.format) {
      formattedPayload.startDate =
        formattedPayload.startDate.format("YYYY-MM-DD");
    }
    if (formattedPayload.endDate && formattedPayload.endDate.format) {
      formattedPayload.endDate = formattedPayload.endDate.format("YYYY-MM-DD");
    }

    setCsvLoading(true);
    await fetchAllGroupsForCSV(formattedPayload);
    setCsvLoading(false);
    // Programmatically trigger CSV download
    setTimeout(() => {
      if (csvLinkRef.current) {
        csvLinkRef.current.link.click();
      }
    }, 0);
  };

  // Fetch groups on component mount
  useEffect(() => {
    // For initial load, don't show embedded loader - let route loader handle it
    fetchGroups({ ...payload, isInitialLoad: true });
  }, [fetchGroups, payload]);

  // API call for search filter
  const setGroupsData = useCallback(
    async (data) => {
      await fetchGroups(data);
    },
    [fetchGroups],
  );

  // Status change
  const changeStatus = async (event, id, index) => {
    setFlag(true);

    let oldReports = JSON.parse(JSON.stringify(groups));
    const value = event.target.value;
    groups[index].isActive = value;

    setGroups([...groups]);

    try {
      await forStatus(id, { status: value });
      copyToClipBoard("status", event.target.value);
    } catch (error) {
      setGroups(oldReports);
      showToast(error?.reason, "error");
    }
    setFlag(false);
  };

  // Filter and search
  const statusHandler = useCallback(
    async (field, event, value) => {
      const newSearchData = { ...payload };

      // Handle different value types - dates, strings, etc.
      const fieldValue = event?.target?.value || value;

      if (
        fieldValue !== null &&
        fieldValue !== undefined &&
        fieldValue !== ""
      ) {
        newSearchData[field] = fieldValue;
        newSearchData.pageNumber = 1;
        setPayload(newSearchData);
      } else {
        delete newSearchData[field];
        setPayload({ ...newSearchData, pageNumber: 1 });
      }

      setGroupsData(newSearchData);
    },
    [payload, setPayload, setGroupsData],
  );

  // Debouncing function call
  const optimizedFn = useCallback(
    (...args) => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
      debounceRef.current = setTimeout(() => {
        statusHandler(...args);
      }, 500);
    },
    [statusHandler],
  );

  // Pagination
  const handlePageClick = (pageNumber) => {
    let newPageConfig = { ...payload, pageNumber: pageNumber + 1 };
    setPayload(newPageConfig);
    fetchGroups(newPageConfig);
  };

  // Delete group handler
  const deleteGroupHandler = async () => {
    await deleteGroup(group?._id);
    fetchGroups();
    showToast("Group deleted succesfully!", "Success");
  };

  // console.log("allGroups", allGroups);
  // console.log("groups", groups);
  // console.log("payload", payload);

  return (
    <>
      <div className="innerHeader">
        {/* page title */}
        <h2>Groups</h2>
      </div>

      <div className="dashboardCardWrap">
        <Card className="dashboardCard">
          <CardHeader>
            Total
            <br />
            Group
            <span className="bgIcon">
              <i className="fa fa-users" />
            </span>
          </CardHeader>
          <CardBody>
            <p>{totalGroup}</p>
          </CardBody>
        </Card>

        <Card className="dashboardCard">
          <CardHeader>
            Subscription
            <br />
            Revenue
            <span className="bgIcon">
              <img
                src={require("../../assets/img/subscription.png")}
                alt="Subscription"
              />
            </span>
          </CardHeader>
          <CardBody>
            <p>{formatCurrencyValue(subscriptionRevenue)}</p>
          </CardBody>
        </Card>

        <Card className="dashboardCard">
          <CardHeader>
            Media
            <br />
            Revenue
            <span className="bgIcon">
              <img src={require("../../assets/img/media.png")} alt="Media" />
            </span>
          </CardHeader>
          <CardBody>
            <p>{formatCurrencyValue(mediaRevenue)}</p>
          </CardBody>
        </Card>

        <Card className="dashboardCard">
          <CardHeader>
            Total
            <br />
            Revenue
            <span className="bgIcon">
              <i className="fas fa-dollar-sign"></i>
            </span>
          </CardHeader>
          <CardBody>
            <p>{formatCurrencyValue(grouprdRevenue)}</p>
          </CardBody>
        </Card>

        <Card className="dashboardCard">
          <CardHeader>
            Grouped
            <br />
            Commission
            <span className="bgIcon">
              <img
                src={require("../../assets/img/commission.png")}
                alt="Commission"
              />
            </span>
          </CardHeader>
          <CardBody>
            <p>
              {formatCurrencyValue(
                (totalGroupSubCom + totalgroupChareged).toFixed(2),
              )}
            </p>
          </CardBody>
        </Card>
      </div>

      {/* filter */}
      <div className="filterWrapper mt-2">
        <div className="filterIcon">
          <i className="fas fa-filter"></i>
        </div>

        <div className="filterForm">
          <div className="formGroup">
            <Label>Members</Label>
            <Input
              type="select"
              onChange={(event) => optimizedFn("memberCount", event)}
            >
              <option value="">All</option>
              <option value="10">10+</option>
              <option value="100">100+</option>
              <option value="1000">1,000+</option>
              <option value="10000">10,000+</option>
            </Input>
          </div>
          <div className="formGroup">
            <Label>Subscription</Label>
            <Input
              type="select"
              onChange={(event) => optimizedFn("isPaid", event)}
            >
              <option value="">All</option>
              <option value="false">Free</option>
              <option value="true">Paid</option>
            </Input>
          </div>

          <div className="formGroup">
            <Label>Status</Label>
            <Input
              type="select"
              onChange={(event) => optimizedFn("status", event)}
            >
              <option value="">All</option>
              <option value="true">Active</option>
              <option value="false">Inactive</option>
            </Input>
          </div>

          <div className="formGroup">
            <Label>Created At</Label>
            <CustomDateRangePicker
              startDate={filters.dateRange.startDate}
              endDate={filters.dateRange.endDate}
              startDateId={"startDate_groups_created"}
              endDateId={`endDate_groups_created`}
              onDatesChange={(startDate, endDate) =>
                onDatesChange(startDate, endDate)
              }
            />
          </div>
          <div className="formGroup">
            <Label>Sort By</Label>
            <Input
              type="select"
              onChange={(event) => optimizedFn("sortBy", event)}
            >
              <option value="Created At">Created At</option>
              <option value="Member Count">Member Count</option>
              <option value="Revenue">Revenue</option>
            </Input>
          </div>
          <div className="formGroup">
            <Label>Order</Label>
            <Input
              type="select"
              onChange={(event) => optimizedFn("sortOrder", event)}
            >
              <option value="high-to-low">High to Low</option>
              <option value="low-to-high">Low to High</option>
            </Input>
          </div>
          <div className="formGroup">
            <Label>Export</Label>
            <div className="mt-1 rounded-xl overflow-hidden bg-black py-2 px-4">
              <button
                className="text-white"
                style={{
                  background: "none",
                  border: "none",
                  cursor: csvLoading ? "not-allowed" : "pointer",
                }}
                onClick={handleCSVExport}
                disabled={csvLoading}
              >
                {csvLoading ? "Preparing..." : "Download"}
              </button>
              <CSVLink
                data={allGroups}
                className="d-none"
                filename="Groups.csv"
                ref={csvLinkRef}
                target="_blank"
              >
                Download
              </CSVLink>
            </div>
          </div>
          {/* search */}
          <div className="formGroup searchbar">
            <Label>Search</Label>
            <InputGroup>
              <Input
                placeholder="Search..."
                onChange={(event) => optimizedFn("searchText", event)}
              />
              <InputGroupText>
                <i className="fas fa-search"></i>
              </InputGroupText>
            </InputGroup>
          </div>
        </div>
      </div>
      <React.Fragment>
        <div className="bg-white p-2 mt-3" style={{ borderRadius: 8 }}>
          {isEmbeddedLoading ? (
            <div
              style={{
                width: "100%",
                display: "flex",
                justifyContent: "center",
                margin: "40px 0",
                minHeight: "200px",
                alignItems: "center",
              }}
            >
              <Oval color="#3498DB" />
            </div>
          ) : (
            <Table responsive>
              <thead>
                <tr>
                  <th>Group</th>
                  <th>Started</th>
                  <th>Leader</th>
                  <th>Members</th>
                  <th>Subscription</th>
                  <th>Total Revenue</th>
                  <th>Grouped Revenue</th>
                  <th>Status</th>
                  <th>Action</th>
                </tr>
              </thead>

              <tbody>
                {groups.map((item, index) => (
                  <tr key={index}>
                    <td
                      className="tableLink"
                      onClick={() => navigate(`/groups-view/${item._id}`)}
                    >
                      {item?.name?.length <= 15
                        ? item?.name.substring(0, 15)
                        : item?.name.substring(0, 15) + "..."}
                    </td>
                    <td>{moment(item.createdAt).format("Do MMM")}</td>
                    <td
                      className="tableLink"
                      onClick={() =>
                        navigate(`/members-view/${item.leader._id}`)
                      }
                    >
                      {item.leader?.firstname} {item.leader?.lastname}
                    </td>
                    <td>
                      <Link to="">{item.memberCount}</Link>
                    </td>
                    <td>
                      {/* the modal should open only for paid plans */}
                      <Link to="" onClick={() => toggle(true, item)}>
                        View Plan
                      </Link>
                      {/* {item?.isPaid ? "$" : null}{item?.isPaid ? item?.subscriptionPlan?.map((data) => {return data.amount.toFixed(2)}) : "free"}{item?.isPaid ? "/Month" : null} */}
                    </td>
                    {/*$ /Month */}
                    <td>
                      <Link to="" onClick={() => toggle1(true, item)}>
                        ${item?.revenue?.totalRevenue.toFixed(2)}
                      </Link>
                    </td>
                    <td>
                      $
                      {(
                        item?.revenue?.totalRevenue -
                        item?.revenue?.groupedRevenue
                      ).toFixed(2)}
                    </td>
                    <td>
                      <div className="action">
                        <Input
                          type="select"
                          value={item.isActive}
                          disabled={flag === true}
                          onChange={(event) =>
                            changeStatus(event, item._id, index)
                          }
                        >
                          <option value="true">Active</option>
                          <option value="false">Inactive</option>
                        </Input>
                      </div>
                    </td>
                    <td>
                      <Button color="link" onClick={() => toggle2(true, item)}>
                        <i className="fa fa-pencil-alt" />
                      </Button>
                      <Button color="link" onClick={() => toggle3(item)}>
                        <i
                          className="fa fa-trash"
                          style={{ marginLeft: 10, color: "red" }}
                        />
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          )}
        </div>
      </React.Fragment>

      <SubscriptionPlanModal
        isOpen={isOpen}
        toggle={() => toggle()}
        subscription={subscription}
      />
      <DeleteAlertModal
        delModeOpen={isDeleteGroupOpen}
        deleteGroupView={() => deleteGroupHandler()}
        toggle={() => setIsDeleteGroupOpen(!isDeleteGroupOpen)}
        groupName={group?.name}
      />

      <TotalRevenueModal
        isOpen={isModalOpen}
        toggle={() => toggle1()}
        totalRevenue={totalRevenue}
      />

      <CommissionPercentageModal
        isOpen={isOpen1}
        toggle={() => toggle2()}
        groupCommision={groupCommision}
        fetchAllGroups={() => fetchGroups()}
      />

      <ToastContainer
        position="bottom-right"
        autoClose={1000}
        hideProgressBar={true}
        newestOnTop={false}
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
      />

      {/* pagination */}
      <PaginatedItems
        itemsPerPages={itemPerPage}
        totalCount={count}
        selectedPage={payload.pageNumber}
        itemsPerPage={payload.pageSize}
        onPageChange={handlePageClick}
      />
    </>
  );
};

export default GroupsPage;
